import { useFocusEffect } from 'expo-router';
import { useCallback } from 'react';

import { create, devtools } from '@fhs/zustand';

import { useChallengesUi } from '../queries/loyalty.queries';

type ChallengesUiHelperState = {
  shouldRefetchChallengeProgressUi: boolean;
};

type ChallengesUiHelperActions = {
  setShouldRefetchChallengeProgressUi: (shouldRefetch: boolean) => void;
};

const initialState: ChallengesUiHelperState = {
  shouldRefetchChallengeProgressUi: false,
};

export const useChallengesUiHelper = create<ChallengesUiHelperState & ChallengesUiHelperActions>()(
  devtools(set => {
    return {
      ...initialState,
      setShouldRefetchChallengeProgressUi: (shouldRefetch: boolean) =>
        set({ shouldRefetchChallengeProgressUi: shouldRefetch }),
    };
  })
);

// We had to create this hook to force a refetch on pages that renders the progress of the challenges
// Because as soon as we commit the order, it takes some time for challenges progress to be updated
// So we add this hook to mitigate the issue and refetch next time the page is focused
// Instead of only invalidating the query
export const useChallengesWithProgressUi = () => {
  const { shouldRefetchChallengeProgressUi, setShouldRefetchChallengeProgressUi } =
    useChallengesUiHelper();

  const challengesUi = useChallengesUi();
  const { refetch } = challengesUi;

  useFocusEffect(
    useCallback(() => {
      if (shouldRefetchChallengeProgressUi) {
        refetch();
        setShouldRefetchChallengeProgressUi(false);
      }
    }, [refetch, shouldRefetchChallengeProgressUi, setShouldRefetchChallengeProgressUi])
  );

  return challengesUi;
};
